import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { TrendingUp, DollarSign, AlertCircle, Target, BarChart, Settings, HelpCircle, X, AlertTriangle } from 'lucide-react';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LabelList, LineChart, Line, AreaChart, Area, ReferenceLine } from 'recharts';
import MarkupConfigModal from '@/components/MarkupConfigModal';
import MarkupDonutChart from '@/components/MarkupDonutChart';
import DREChart from '@/components/DREChart';
import FluxoCaixaDashboard from '@/components/FluxoCaixaDashboard';
import DFCResumoCard from '@/components/DFCResumoCard';
import GPSFinanceiro from '@/components/GPSFinanceiro';

// Componente de Botão de Ajuda
const HelpButton = ({ title, description, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`w-6 h-6 rounded-full bg-slate-700/50 hover:bg-slate-600 flex items-center justify-center transition-colors ${className}`}
        title="Clique para saber mais"
      >
        <HelpCircle className="w-4 h-4 text-slate-400 hover:text-white" />
      </button>
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="glass border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-purple-400" />
              {title}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">
            {description}
          </DialogDescription>
          <Button 
            onClick={() => setIsOpen(false)}
            className="w-full mt-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
          >
            Entendi!
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Componente de Título de Seção
const SectionTitle = ({ icon: Icon, title, helpTitle, helpDescription }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-3">
      {Icon && (
        <div className="p-2 rounded-lg bg-gradient-to-br from-purple-600 to-blue-600">
          <Icon className="w-5 h-5 text-white" />
        </div>
      )}
      <h2 className="text-lg font-bold text-white">{title}</h2>
    </div>
    {helpTitle && helpDescription && (
      <HelpButton title={helpTitle} description={helpDescription} />
    )}
  </div>
);

// Definições de ajuda para cada card e seção
const HELP_CONTENT = {
  // Seções
  cro_mes: {
    title: "CRO do Mês",
    description: `O CRO (Controle de Resultado Operacional) mostra o resumo financeiro do mês atual.

📊 A barra mostra visualmente a proporção entre receitas e despesas.

💰 Receita Líquida: Tudo que a empresa recebeu no mês.

💸 Custos + Despesas: Gastos com materiais, mão de obra e despesas administrativas.

📈 Lucro Líquido: O que sobrou após pagar todas as contas. 
   → Margem positiva = empresa lucrando
   → Margem negativa = prejuízo no mês`
  },
  fluxo_caixa: {
    title: "Fluxo de Caixa",
    description: `O Fluxo de Caixa projeta como será o dinheiro em caixa nos próximos meses.

📅 Mostra dia a dia quanto você terá disponível.

🔵 Linha azul: Saldo projetado considerando entradas e saídas previstas.

⚠️ Se a linha ficar negativa, significa que pode faltar dinheiro em caixa naquele período.

💡 Use para planejar pagamentos e evitar surpresas!`
  },
  kpis_principais: {
    title: "Indicadores do Mês",
    description: `Os KPIs (Key Performance Indicators) mostram os números mais importantes do mês atual:

💰 Faturamento: Total de vendas realizadas no mês.

🔧 Custos: Gastos diretos com materiais e mão de obra das obras.

📋 Despesas: Gastos administrativos (aluguel, contador, etc).

📊 Lucro Líquido: Faturamento - Custos - Despesas = Lucro.`
  },
  contas_resumo: {
    title: "Resumo de Contas",
    description: `Visão rápida das suas contas a pagar e receber:

🔴 Total a Pagar: Quanto você deve para fornecedores.

🟢 Total a Receber: Quanto os clientes devem para você.

🔵 Saldo Projetado: Receber - Pagar = Se positivo, você terá sobra.

⚠️ Contas Atrasadas: Atenção! Contas vencidas que precisam de ação.`
  },
  graficos_contas: {
    title: "Gráficos de Contas",
    description: `Visualização detalhada das suas contas:

🔴 Contas a Pagar por Categoria: Mostra onde você mais gasta (materiais, mão de obra, etc).

🟢 Contas a Receber por Cliente: Mostra quais clientes mais devem.

📊 Use para identificar concentração de gastos ou clientes inadimplentes.`
  },
  evolucao: {
    title: "Evolução (6 meses)",
    description: `Gráfico histórico dos últimos 6 meses:

🟣 Faturamento: Quanto a empresa vendeu em cada mês.

🔵 Lucro Líquido: Quanto sobrou de lucro em cada mês.

📈 Compare os meses para identificar tendências:
   → Crescimento = barras cada vez maiores
   → Estabilidade = barras similares
   → Queda = barras diminuindo`
  },
  // Cards individuais
  faturamento: {
    title: "Faturamento",
    description: `Total de vendas e serviços realizados no mês atual.

📝 Inclui todas as notas fiscais emitidas e receitas registradas.

💡 Um faturamento alto nem sempre significa lucro alto - é preciso controlar os custos!`
  },
  custos: {
    title: "Custos",
    description: `Gastos diretamente ligados à produção/serviços:

🧱 Materiais e insumos
👷 Mão de obra direta
🚛 Frete de materiais

💡 Quanto menor o custo mantendo a qualidade, maior o lucro!`
  },
  despesas: {
    title: "Despesas",
    description: `Gastos administrativos e operacionais:

🏢 Aluguel e contas do escritório
📱 Telefone, internet
💼 Contador, advogado
🚗 Combustível administrativo

💡 Despesas são custos fixos que existem mesmo sem produção.`
  },
  lucro_liquido: {
    title: "Lucro Líquido",
    description: `O que sobra após pagar tudo:

📊 Faturamento - Custos - Despesas = Lucro Líquido

🟢 Positivo: A empresa está lucrando
🔴 Negativo: A empresa está no prejuízo

💡 Acompanhe mensalmente para garantir a saúde do negócio.`
  },
  total_pagar: {
    title: "Total a Pagar",
    description: `Soma de todas as contas pendentes com fornecedores:

📋 Inclui compras a prazo, parcelamentos e boletos em aberto.

⚠️ Fique atento aos vencimentos para evitar juros e multas!`
  },
  total_receber: {
    title: "Total a Receber",
    description: `Soma de todos os valores que os clientes devem:

💰 Parcelas de obras, serviços prestados, vendas a prazo.

💡 Acompanhe de perto e faça cobranças pontuais para manter o caixa saudável.`
  },
  saldo_projetado: {
    title: "Saldo Projetado",
    description: `Previsão do seu saldo considerando:

📥 Total a Receber (entradas)
📤 Total a Pagar (saídas)

🔵 Positivo: Você terá sobra de caixa
🔴 Negativo: Pode faltar dinheiro

💡 Use para planejar investimentos ou negociar prazos.`
  },
  contas_atrasadas: {
    title: "Contas Atrasadas",
    description: `⚠️ ATENÇÃO: Contas com vencimento passado!

🔴 Pagar: Você pode estar pagando juros/multas
🟠 Receber: Clientes inadimplentes

💡 Priorize regularizar essas pendências para evitar problemas.`
  }
};

const Dashboard = ({ user, onLogout }) => {
  const isFuncionarioSis = user?.categoria === 'FuncionárioSis';
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showMarkupModal, setShowMarkupModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyData, setCompanyData] = useState({ name: '', segment: '' });
  const [metrics, setMetrics] = useState(null);
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [contasResumo, setContasResumo] = useState(null);
  const [fluxoCaixaData, setFluxoCaixaData] = useState([]);
  const [contasPagarPorCategoria, setContasPagarPorCategoria] = useState([]);
  const [contasReceberPorCliente, setContasReceberPorCliente] = useState([]);
  const [markupRefreshKey, setMarkupRefreshKey] = useState(0);
  // Estado para Lucro Líquido do Mês
  const [lucroMesAtual, setLucroMesAtual] = useState({ receitas: 0, despesas: 0, lucro: 0 });
  const [alertasDash, setAlertasDash] = useState([]);
  const [proximosVencimentos, setProximosVencimentos] = useState([]);
  const [lancamentosRecentes, setLancamentosRecentes] = useState([]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      if (isFuncionarioSis) {
        fetchContasResumo();
        fetchProximosVencimentos();
        fetchLancamentosRecentes();
      } else {
        fetchMetrics();
        fetchGoal();
        generateChartData();
        fetchContasResumo();
        fetchFluxoCaixa();
        fetchContasPagarPorCategoria();
        fetchContasReceberPorCliente();
        fetchAlertasDash();
      }
    }
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    try {
      // Se for funcionário, carregar empresa pelo company_id
      if (user?.role === 'FUNCIONARIO' || user?.role === 'funcionario') {
        const stored = localStorage.getItem('company');
        if (stored) {
          const storedCompany = JSON.parse(stored);
          setSelectedCompany(storedCompany);
          setCompanies([storedCompany]);
        } else if (user?.company_id) {
          const res = await axiosInstance.get(`/company/${user.company_id}`);
          const c = res.data;
          setSelectedCompany(c);
          setCompanies([c]);
          localStorage.setItem('company', JSON.stringify(c));
        } else {
          toast.error('Empresa não encontrada. Faça login novamente.');
        }
        return;
      }
      
      // Se for gestor, buscar empresas normalmente
      const response = await axiosInstance.get(`/companies/${(user?.id || user?.user_id)}`);
      const companiesList = response.data;
      
      if (companiesList.length > 0) {
        const stored = localStorage.getItem('company');
        if (stored) {
          const storedCompany = JSON.parse(stored);
          const exists = companiesList.find(c => c.id === storedCompany.id);
          setSelectedCompany(exists || companiesList[0]);
        } else {
          setSelectedCompany(companiesList[0]);
          localStorage.setItem('company', JSON.stringify(companiesList[0]));
        }
      } else {
        setShowCompanyModal(true);
      }
      
      setCompanies(companiesList);
    } catch (error) {
      toast.error('Erro ao carregar empresas');
    }
  };

  const createCompany = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axiosInstance.post('/companies', {
        user_id: (user?.id || user?.user_id),
        ...companyData,
      });
      toast.success('Empresa criada com sucesso!');
      setShowCompanyModal(false);
      fetchCompanies();
    } catch (error) {
      toast.error('Erro ao criar empresa');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    if (!selectedCompany) return;

    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const response = await axiosInstance.get(`/metrics/${selectedCompany.id}/${currentMonth}`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Erro ao buscar métricas:', error);
    }
  };

  const fetchGoal = async () => {
    if (!selectedCompany) return;

    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const response = await axiosInstance.get(`/monthly-goal/${selectedCompany.id}/${currentMonth}`);
      setGoal(response.data);
    } catch (error) {
      console.error('Erro ao buscar meta:', error);
    }
  };

  const generateChartData = async () => {
    if (!selectedCompany) return;

    try {
      // Buscar dados dos últimos 6 meses
      const data = [];
      const now = new Date();
      
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const month = date.toISOString().slice(0, 7);
        const monthLabel = date.toLocaleDateString('pt-BR', { month: 'short' });
        
        const response = await axiosInstance.get(`/metrics/${selectedCompany.id}/${month}`);
        
        data.push({
          month: monthLabel,
          faturamento: response.data.faturamento,
          lucro: response.data.lucro_liquido,
        });
      }
      
      setChartData(data);
    } catch (error) {
      console.error('Erro ao gerar dados do gráfico:', error);
    }
  };

  const getGoalProgress = () => {
    if (!metrics || !goal || goal.goal_amount === 0) return 0;
    return Math.min((metrics.lucro_liquido / goal.goal_amount) * 100, 100);
  };

  // Funções de IA removidas para simplificar o sistema

  // Análise detalhada removida

  const fetchContasResumo = async () => {
    if (!selectedCompany) return;

    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const response = await axiosInstance.get(`/contas/resumo-mensal?company_id=${selectedCompany.id}&mes=${currentMonth}`);
      setContasResumo(response.data);
    } catch (error) {
      console.error('Erro ao buscar resumo de contas:', error);
    }
  };

  // Atualizar CRO quando as métricas mudarem (usa mesma fonte dos KPIs)
  useEffect(() => {
    if (metrics) {
      // CRO usa receita líquida (já descontando impostos) para ficar igual à DRE
      const receitas = metrics.receita_liquida || metrics.faturamento || 0;
      const despesas = (metrics.custos || 0) + (metrics.despesas || 0);
      const lucro = metrics.lucro_liquido || 0;
      const impostos = metrics.impostos || 0;
      setLucroMesAtual({ receitas, despesas, lucro, impostos });
    }
  }, [metrics]);

  const fetchFluxoCaixa = async () => {
    if (!selectedCompany) return;

    try {
      const response = await axiosInstance.get(`/contas/fluxo-caixa-projetado?company_id=${selectedCompany.id}&meses=6`);
      setFluxoCaixaData(response.data);
    } catch (error) {
      console.error('Erro ao buscar fluxo de caixa:', error);
    }
  };

  const fetchContasPagarPorCategoria = async () => {
    if (!selectedCompany) return;

    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const response = await axiosInstance.get(`/contas/pagar-por-categoria?company_id=${selectedCompany.id}&mes=${currentMonth}`);
      setContasPagarPorCategoria((response.data || []).slice().sort((a, b) => (a.categoria || '').localeCompare(b.categoria || '', 'pt-BR', { sensitivity: 'base' })));
    } catch (error) {
      console.error('Erro ao buscar contas a pagar por categoria:', error);
    }
  };

  const fetchContasReceberPorCliente = async () => {
    if (!selectedCompany) return;

    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const response = await axiosInstance.get(`/contas/receber-por-cliente?company_id=${selectedCompany.id}&mes=${currentMonth}`);
      setContasReceberPorCliente(response.data);
    } catch (error) {
      console.error('Erro ao buscar contas a receber por cliente:', error);
    }
  };

  const fetchAlertasDash = async () => {
    if (!selectedCompany) return;
    try {
      const response = await axiosInstance.get(`/relatorios/alertas/${selectedCompany.id}`);
      setAlertasDash(response.data?.alertas || []);
    } catch (error) {
      console.error('Erro ao buscar alertas:', error);
    }
  };

  const fetchProximosVencimentos = async () => {
    if (!selectedCompany) return;
    try {
      const response = await axiosInstance.get(`/contas/proximos-vencimentos?company_id=${selectedCompany.id}&dias=7`);
      setProximosVencimentos(response.data.contas || []);
    } catch (error) {
      console.error('Erro ao buscar próximos vencimentos:', error);
    }
  };

  const fetchLancamentosRecentes = async () => {
    if (!selectedCompany) return;
    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const response = await axiosInstance.get(`/transactions/${selectedCompany.id}?month=${currentMonth}`);
      setLancamentosRecentes((response.data || []).slice(0, 10));
    } catch (error) {
      console.error('Erro ao buscar lançamentos recentes:', error);
    }
  };

  // ===== MESA OPERACIONAL — FuncionárioSis =====
  if (isFuncionarioSis) {
    return (
      <div className="flex min-h-screen">
        <Sidebar user={user} onLogout={onLogout} />
        <div className="flex-1 p-3 pt-14 md:p-8 overflow-x-hidden" style={{ backgroundColor: '#EEF2FF' }}>

          {/* Header */}
          <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#1E3A8A' }}>
            <h1 className="text-2xl font-bold text-white">Painel Operacional</h1>
            {selectedCompany && (
              <p className="text-sm text-white mt-1" style={{ opacity: 0.8 }}>
                {selectedCompany.name} · {selectedCompany.segment}
              </p>
            )}
          </div>

          {/* Atalhos Rápidos */}
          <div className="mb-6 grid grid-cols-3 gap-3">
            {[
              { label: 'Novo Lançamento', to: '/lancamentos' },
              { label: 'Conta a Pagar', to: '/contas/pagar' },
              { label: 'Conta a Receber', to: '/contas/receber' },
            ].map(({ label, to }) => (
              <Link
                key={to}
                to={to}
                className="flex items-center justify-center text-center text-sm font-bold rounded-xl p-3 transition-opacity hover:opacity-80"
                style={{ backgroundColor: '#1E3A8A', color: '#fff' }}
              >
                {label}
              </Link>
            ))}
          </div>

          {/* Alerta de Vencidas */}
          {contasResumo && (
            <div
              className="mb-6 p-4 rounded-2xl border-2"
              style={
                contasResumo.contas_atrasadas.total > 0
                  ? { borderColor: '#EF4444', backgroundColor: '#FEF2F2' }
                  : { borderColor: '#10B981', backgroundColor: '#F0FDF4' }
              }
            >
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle
                  className="w-5 h-5"
                  style={{ color: contasResumo.contas_atrasadas.total > 0 ? '#EF4444' : '#10B981' }}
                />
                <span className="font-bold text-black">
                  {contasResumo.contas_atrasadas.total > 0
                    ? 'Contas Atrasadas — Ação Necessária!'
                    : 'Nenhuma conta atrasada ✓'}
                </span>
              </div>
              {contasResumo.contas_atrasadas.total > 0 ? (
                <>
                  <p className="text-2xl font-black" style={{ color: '#EF4444' }}>
                    R$ {contasResumo.contas_atrasadas.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-black mt-1">
                    A Pagar: R$ {contasResumo.pagar.atrasado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} ·{' '}
                    A Receber: R$ {contasResumo.receber.atrasado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </>
              ) : (
                <p className="text-xs text-black mt-0.5">Todas as contas estão dentro do prazo</p>
              )}
            </div>
          )}

          {/* Resumo Operacional */}
          {contasResumo && (
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl border p-5" style={{ backgroundColor: '#FFFFFF', borderColor: '#DBEAFE' }}>
                <p className="text-xs font-bold uppercase tracking-wider text-black mb-2">Total a Pagar</p>
                <p className="text-3xl font-black" style={{ color: '#EF4444' }}>
                  R$ {contasResumo.pagar.pendente.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-black mt-1">{contasResumo.pagar.quantidade_total} conta(s) pendente(s)</p>
              </div>
              <div className="rounded-2xl border p-5" style={{ backgroundColor: '#FFFFFF', borderColor: '#DBEAFE' }}>
                <p className="text-xs font-bold uppercase tracking-wider text-black mb-2">Total a Receber</p>
                <p className="text-3xl font-black" style={{ color: '#10B981' }}>
                  R$ {contasResumo.receber.pendente.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-black mt-1">{contasResumo.receber.quantidade_total} conta(s) pendente(s)</p>
              </div>
            </div>
          )}

          {/* Próximos Vencimentos */}
          <div className="mb-6 rounded-2xl border p-5" style={{ backgroundColor: '#FFFFFF', borderColor: '#DBEAFE' }}>
            <h2 className="font-bold text-black mb-4">Vencimentos nos próximos 7 dias</h2>
            {proximosVencimentos.length === 0 ? (
              <p className="text-sm text-black">Nenhum vencimento nos próximos 7 dias.</p>
            ) : (
              <div className="space-y-2">
                {proximosVencimentos.map((conta) => (
                  <div
                    key={conta.id}
                    className="flex items-center justify-between p-3 rounded-xl"
                    style={{ backgroundColor: '#DBEAFE' }}
                  >
                    <div>
                      <p className="font-semibold text-black text-sm">{conta.descricao}</p>
                      <p className="text-xs text-black">
                        {conta.tipo === 'PAGAR' ? 'A Pagar' : 'A Receber'} · Vence:{' '}
                        {new Date(conta.data_vencimento + 'T12:00:00').toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm" style={{ color: conta.tipo === 'PAGAR' ? '#EF4444' : '#10B981' }}>
                        R$ {conta.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                      <span className="text-xs font-semibold" style={{ color: '#1E3A8A' }}>{conta.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Lançamentos Recentes */}
          {lancamentosRecentes.length > 0 && (
            <div className="mb-6 rounded-2xl border p-5" style={{ backgroundColor: '#FFFFFF', borderColor: '#DBEAFE' }}>
              <h2 className="font-bold text-black mb-4">
                Lançamentos Recentes —{' '}
                {new Date().toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}
              </h2>
              <div className="space-y-2">
                {lancamentosRecentes.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center justify-between p-3 rounded-xl"
                    style={{ backgroundColor: '#DBEAFE' }}
                  >
                    <div>
                      <p className="font-semibold text-black text-sm">{t.description}</p>
                      <p className="text-xs text-black">
                        {t.category} · {new Date(t.date + 'T12:00:00').toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <p className="font-bold text-sm" style={{ color: t.type === 'receita' ? '#10B981' : '#EF4444' }}>
                      {t.type === 'receita' ? '+' : '-'} R${' '}
                      {t.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    );
  }

  // Função de análise com IA removida

  return (
    <div className="flex min-h-screen" data-testid="dashboard-page">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 p-3 pt-14 md:p-8 overflow-x-hidden">
        {/* Header com Status de Assinatura */}
        <div className="flex items-start justify-between mb-4 md:mb-8 gap-2 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-white mb-2" data-testid="dashboard-title">Dashboard</h1>
            {selectedCompany && (
              <p className="text-gray-400" data-testid="selected-company-name">
                {selectedCompany.name} - {selectedCompany.segment}
              </p>
            )}
          </div>
          <SubscriptionCard user={user} compact={true} />
        </div>

        {/* ===== DESTAQUE FINANCEIRO: Lucro + Contas Atrasadas ===== */}
        {(metrics || contasResumo) && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Lucro Líquido em Destaque */}
            {metrics && (
              <div
                className="p-6 rounded-2xl border-2"
                style={{
                  background: metrics.lucro_liquido >= 0
                    ? 'linear-gradient(135deg, rgba(6,78,59,0.6) 0%, rgba(8,51,68,0.5) 100%)'
                    : 'linear-gradient(135deg, rgba(127,29,29,0.6) 0%, rgba(88,28,28,0.5) 100%)',
                  borderColor: metrics.lucro_liquido >= 0 ? 'rgba(52,211,153,0.5)' : 'rgba(248,113,113,0.5)',
                  boxShadow: metrics.lucro_liquido >= 0 ? '0 0 40px rgba(16,185,129,0.12)' : '0 0 40px rgba(244,63,94,0.12)',
                }}
              >
                <p className={`text-xs font-semibold uppercase tracking-widest mb-2 ${metrics.lucro_liquido >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  Lucro Líquido — {new Date().toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}
                </p>
                <p
                  className={`text-3xl md:text-5xl font-black mb-1 ${metrics.lucro_liquido >= 0 ? 'text-cyan-300' : 'text-rose-300'}`}
                  style={{ textShadow: metrics.lucro_liquido >= 0 ? '0 0 24px rgba(0,255,255,0.4)' : '0 0 24px rgba(248,113,113,0.4)' }}
                >
                  {metrics.lucro_liquido >= 0 ? '+' : '-'} R$ {(Math.abs(metrics.lucro_liquido) / 1000).toFixed(1)}k
                </p>
                {(metrics.receita_liquida || metrics.faturamento) > 0 && (
                  <p className={`text-sm font-semibold ${metrics.lucro_liquido >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    Margem: {((metrics.lucro_liquido / (metrics.receita_liquida || metrics.faturamento)) * 100).toFixed(1)}%
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Faturamento: R$ {(metrics.faturamento / 1000).toFixed(1)}k · Custos+Desp: R$ {((metrics.custos + metrics.despesas) / 1000).toFixed(1)}k
                </p>
              </div>
            )}

            {/* Contas Atrasadas */}
            {contasResumo && (
              <div
                className="p-6 rounded-2xl border-2"
                style={{
                  background: contasResumo.contas_atrasadas.total > 0
                    ? 'linear-gradient(135deg, rgba(127,29,29,0.5) 0%, rgba(124,45,18,0.4) 100%)'
                    : 'linear-gradient(135deg, rgba(6,78,59,0.4) 0%, rgba(3,56,43,0.3) 100%)',
                  borderColor: contasResumo.contas_atrasadas.total > 0 ? 'rgba(239,68,68,0.6)' : 'rgba(52,211,153,0.3)',
                  boxShadow: contasResumo.contas_atrasadas.total > 0 ? '0 0 30px rgba(239,68,68,0.15)' : 'none',
                }}
              >
                <p className={`text-xs font-semibold uppercase tracking-widest mb-2 flex items-center gap-1 ${
                  contasResumo.contas_atrasadas.total > 0 ? 'text-red-400' : 'text-emerald-400'
                }`}>
                  <AlertTriangle className="w-3 h-3" />
                  {contasResumo.contas_atrasadas.total > 0 ? 'Contas Atrasadas — Ação Necessária!' : 'Nenhuma Conta Atrasada ✓'}
                </p>
                <p className={`text-3xl md:text-5xl font-black mb-1 ${contasResumo.contas_atrasadas.total > 0 ? 'text-red-300' : 'text-emerald-400'}`}>
                  R$ {(contasResumo.contas_atrasadas.total / 1000).toFixed(1)}k
                </p>
                {contasResumo.contas_atrasadas.total > 0 ? (
                  <p className="text-xs text-red-400/80 mt-1">
                    A Pagar: R$ {contasResumo.pagar.atrasado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} · A Receber: R$ {contasResumo.receber.atrasado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                ) : (
                  <p className="text-xs text-emerald-500/70 mt-1">Todas as contas estão dentro do prazo</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ===== ALERTAS DE RISCO ===== */}
        {alertasDash.length > 0 && (
          <div className="mb-6 rounded-2xl border border-orange-500/30 bg-slate-800/50 p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              <h3 className="text-white font-bold text-sm">Alertas de Risco</h3>
              <span className="ml-auto bg-orange-500/20 text-orange-300 text-xs font-bold px-2 py-0.5 rounded-full border border-orange-500/30">
                {alertasDash.length} ativo(s)
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {alertasDash.map((a, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-xl border text-xs ${
                    a.severity === 'critico'
                      ? 'bg-red-900/30 border-red-500/40 text-red-200'
                      : a.severity === 'alto'
                      ? 'bg-orange-900/30 border-orange-500/40 text-orange-200'
                      : 'bg-yellow-900/20 border-yellow-500/30 text-yellow-200'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-base flex-shrink-0">
                      {a.severity === 'critico' ? '🚨' : a.severity === 'alto' ? '⚠️' : '🟡'}
                    </span>
                    <div>
                      <p className="font-bold mb-0.5">{a.titulo}</p>
                      <p className="opacity-75 leading-relaxed">{a.descricao}</p>
                      {a.valor > 0 && (
                        <p className="font-black mt-1">R$ {a.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      )}
                      {a.acao && <p className="mt-1 opacity-60 italic">{a.acao}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Termômetro de Meta - Primeiro elemento do Dashboard */}
        {goal && goal.goal_amount > 0 && metrics && (
          <Card className="glass border-white/10 mb-6" data-testid="goal-thermometer-card">
            <CardHeader>
              <CardTitle className="flex items-center text-white">
                <Target className="mr-2" />
                Termômetro de Meta Mensal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between text-sm text-gray-400">
                  <span>Lucro Atual: R$ {metrics.lucro_liquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  <span>Meta: R$ {goal.goal_amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-6 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-600 to-blue-600 transition-all duration-500 flex items-center justify-center"
                    style={{ width: `${getGoalProgress()}%` }}
                    data-testid="goal-progress-bar"
                  >
                    <span className="text-xs font-bold text-white">{getGoalProgress().toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* GPS Financeiro */}
        {selectedCompany && (
          <GPSFinanceiro companyId={selectedCompany.id} />
        )}

        {/* CRO do Mês - Card Principal */}
        {lucroMesAtual && (
          <div className="mb-6">
            <SectionTitle 
              icon={TrendingUp}
              title="Resultado do Mês"
              helpTitle={HELP_CONTENT.cro_mes.title}
              helpDescription={HELP_CONTENT.cro_mes.description}
            />
            <div className="rounded-xl p-4 lg:p-5 bg-gradient-to-r from-slate-800/80 via-slate-800/60 to-slate-900/80 border border-slate-700/50" style={{ boxShadow: '0 0 30px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)' }}>
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                {/* Título e Mês */}
                <div className="flex items-center gap-3 min-w-[200px]">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500" style={{ boxShadow: '0 0 20px rgba(16,185,129,0.4)' }}>
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base">CRO do Mês</h3>
                    <p className="text-xs text-slate-400">
                      {new Date().toLocaleString('pt-BR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())} / {new Date().getFullYear()}
                    </p>
                  </div>
                </div>

                {/* Barra de Progresso Visual */}
                <div className="flex-1 max-w-xl">
                  <div className="h-8 rounded-full bg-slate-700/50 overflow-hidden flex relative" style={{ boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)' }}>
                    {/* Barra de Receitas */}
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 transition-all duration-700 flex items-center justify-center relative"
                      style={{ 
                        width: `${Math.min((lucroMesAtual.receitas / (lucroMesAtual.receitas + lucroMesAtual.despesas)) * 100, 100)}%`,
                        boxShadow: '0 0 15px rgba(16,185,129,0.5)'
                      }}
                    >
                      <span className="text-xs font-bold text-white drop-shadow-lg px-2 whitespace-nowrap">
                        Receitas
                      </span>
                    </div>
                    {/* Barra de Despesas */}
                    <div 
                      className="h-full bg-gradient-to-r from-orange-500 to-rose-500 transition-all duration-700 flex items-center justify-center"
                      style={{ 
                        width: `${Math.min((lucroMesAtual.despesas / (lucroMesAtual.receitas + lucroMesAtual.despesas)) * 100, 100)}%`,
                        boxShadow: '0 0 15px rgba(249,115,22,0.5)'
                      }}
                    >
                      <span className="text-xs font-bold text-white drop-shadow-lg px-2 whitespace-nowrap">
                        Despesas
                      </span>
                    </div>
                  </div>
                </div>

                {/* Valores */}
                <div className="flex items-center gap-4 lg:gap-6">
                  {/* Receita Líquida */}
                  <div className="text-center">
                    <p className="text-xs text-emerald-400 font-medium uppercase tracking-wider">Receita Líq.</p>
                    <p className="text-lg font-bold text-emerald-400" style={{ textShadow: '0 0 10px rgba(16,185,129,0.5)' }}>
                      R$ {(lucroMesAtual.receitas / 1000).toFixed(1)}k
                    </p>
                  </div>

                  {/* Custos + Despesas */}
                  <div className="text-center">
                    <p className="text-xs text-orange-400 font-medium uppercase tracking-wider">Custos+Desp.</p>
                    <p className="text-lg font-bold text-orange-400" style={{ textShadow: '0 0 10px rgba(249,115,22,0.5)' }}>
                      R$ {(lucroMesAtual.despesas / 1000).toFixed(1)}k
                    </p>
                  </div>

                  {/* Separador */}
                  <div className="hidden lg:block w-px h-10 bg-slate-600" />

                  {/* Lucro Líquido */}
                  <div className="text-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-900/50 to-cyan-900/50 border border-purple-500/30" style={{ boxShadow: '0 0 20px rgba(124,58,237,0.2)' }}>
                    <p className="text-xs text-purple-300 font-medium uppercase tracking-wider">Lucro Líquido</p>
                    <p className={`text-xl font-black ${lucroMesAtual.lucro >= 0 ? 'text-cyan-400' : 'text-rose-400'}`} 
                       style={{ textShadow: lucroMesAtual.lucro >= 0 ? '0 0 15px rgba(0,255,255,0.6)' : '0 0 15px rgba(244,63,94,0.6)' }}>
                      {lucroMesAtual.lucro >= 0 ? '+' : '-'}R$ {(Math.abs(lucroMesAtual.lucro) / 1000).toFixed(1)}k
                    </p>
                    {lucroMesAtual.receitas > 0 && (
                      <p className={`text-xs font-semibold ${lucroMesAtual.lucro >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {((lucroMesAtual.lucro / lucroMesAtual.receitas) * 100).toFixed(1)}% margem
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fluxo de Caixa Dashboard - Seção Completa */}
        <div className="mb-8">
          <SectionTitle 
            icon={BarChart}
            title="Fluxo de Caixa Projetado"
            helpTitle={HELP_CONTENT.fluxo_caixa.title}
            helpDescription={HELP_CONTENT.fluxo_caixa.description}
          />
          <FluxoCaixaDashboard companyId={selectedCompany?.id} />
        </div>

        {/* KPIs */}
        {metrics && (
          <div className="mb-8">
            <SectionTitle 
              icon={DollarSign}
              title="Indicadores do Mês"
              helpTitle={HELP_CONTENT.kpis_principais.title}
              helpDescription={HELP_CONTENT.kpis_principais.description}
            />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="glass border-white/10 hover-lift relative" data-testid="kpi-faturamento">
                <HelpButton 
                  title={HELP_CONTENT.faturamento.title} 
                  description={HELP_CONTENT.faturamento.description}
                  className="absolute top-3 right-3"
                />
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-400">Faturamento</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-white">
                        R$ {metrics.faturamento.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <DollarSign className="text-green-400" size={32} />
                  </div>
                </CardContent>
              </Card>

              <Card className="glass border-white/10 hover-lift relative" data-testid="kpi-custos">
                <HelpButton 
                  title={HELP_CONTENT.custos.title} 
                  description={HELP_CONTENT.custos.description}
                  className="absolute top-3 right-3"
                />
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-400">Custos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-white">
                        R$ {metrics.custos.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <AlertCircle className="text-orange-400" size={32} />
                  </div>
                </CardContent>
              </Card>

              <Card className="glass border-white/10 hover-lift relative" data-testid="kpi-despesas">
                <HelpButton 
                  title={HELP_CONTENT.despesas.title} 
                  description={HELP_CONTENT.despesas.description}
                  className="absolute top-3 right-3"
                />
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-400">Despesas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-white">
                        R$ {metrics.despesas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <AlertCircle className="text-red-400" size={32} />
                  </div>
                </CardContent>
              </Card>

              <Card className="hover-lift relative border-2 border-cyan-500/40 bg-gradient-to-br from-slate-800 to-cyan-950/30" data-testid="kpi-lucro"
                style={{ boxShadow: '0 0 20px rgba(6,182,212,0.1)' }}>
                <HelpButton
                  title={HELP_CONTENT.lucro_liquido.title}
                  description={HELP_CONTENT.lucro_liquido.description}
                  className="absolute top-3 right-3"
                />
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-cyan-400 uppercase tracking-wider">Lucro Líquido</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-3xl font-black ${metrics.lucro_liquido >= 0 ? 'text-cyan-300' : 'text-rose-400'}`}
                         style={{ textShadow: metrics.lucro_liquido >= 0 ? '0 0 12px rgba(0,255,255,0.35)' : '0 0 12px rgba(248,113,113,0.35)' }}>
                        {metrics.lucro_liquido >= 0 ? '+' : '-'} R$ {(Math.abs(metrics.lucro_liquido) / 1000).toFixed(1)}k
                      </p>
                      {(metrics.receita_liquida || metrics.faturamento) > 0 && (
                        <p className={`text-xs font-semibold mt-1 ${metrics.lucro_liquido >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          Margem {((metrics.lucro_liquido / (metrics.receita_liquida || metrics.faturamento)) * 100).toFixed(1)}%
                        </p>
                      )}
                    </div>
                    <TrendingUp className="text-cyan-400" size={32} />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* KPIs de Contas a Pagar/Receber */}
        {contasResumo && (
          <div className="mb-8">
            <SectionTitle 
              icon={AlertCircle}
              title="Resumo de Contas"
              helpTitle={HELP_CONTENT.contas_resumo.title}
              helpDescription={HELP_CONTENT.contas_resumo.description}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="glass border-white/10 hover-lift border-l-4 border-l-red-500 relative" data-testid="kpi-contas-pagar">
                <HelpButton 
                  title={HELP_CONTENT.total_pagar.title} 
                  description={HELP_CONTENT.total_pagar.description}
                  className="absolute top-3 right-3"
                />
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-400">Total a Pagar</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-red-400">
                        R$ {contasResumo.pagar.pendente.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {contasResumo.pagar.quantidade_total} conta(s) pendente(s)
                      </p>
                    </div>
                    <AlertCircle className="text-red-400" size={32} />
                  </div>
                </CardContent>
              </Card>

              <Card className="glass border-white/10 hover-lift border-l-4 border-l-green-500 relative" data-testid="kpi-contas-receber">
                <HelpButton 
                  title={HELP_CONTENT.total_receber.title} 
                  description={HELP_CONTENT.total_receber.description}
                  className="absolute top-3 right-3"
                />
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-400">Total a Receber</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-green-400">
                        R$ {contasResumo.receber.pendente.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {contasResumo.receber.quantidade_total} conta(s) pendente(s)
                      </p>
                    </div>
                    <DollarSign className="text-green-400" size={32} />
                  </div>
                </CardContent>
              </Card>

              <Card className="glass border-white/10 hover-lift border-l-4 border-l-blue-500 relative" data-testid="kpi-saldo-projetado">
                <HelpButton 
                  title={HELP_CONTENT.saldo_projetado.title} 
                  description={HELP_CONTENT.saldo_projetado.description}
                  className="absolute top-3 right-3"
                />
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-400">Saldo Projetado</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-2xl font-bold ${contasResumo.saldo_projetado >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                        R$ {contasResumo.saldo_projetado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {contasResumo.saldo_projetado >= 0 ? 'Positivo' : 'Negativo'}
                      </p>
                    </div>
                    <TrendingUp className={contasResumo.saldo_projetado >= 0 ? 'text-blue-400' : 'text-red-400'} size={32} />
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>
        )}

        {/* Gráfico de Evolução */}
        <Card className="glass border-white/10" data-testid="evolution-chart-card">
          <CardHeader>
            <CardTitle className="flex items-center text-white">
              <BarChart className="mr-2" />
              Evolução (6 meses)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <RechartsBarChart data={chartData} barSize={60} margin={{ top: 30, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="month" 
                  stroke="#9CA3AF" 
                  style={{ fontSize: '14px', fontWeight: 500 }}
                />
                <YAxis 
                  stroke="#9CA3AF"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #7C3AED',
                    borderRadius: '8px',
                    padding: '12px'
                  }}
                  labelStyle={{ color: '#F3F4F6', fontWeight: 'bold', marginBottom: '8px' }}
                  formatter={(value) => [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, '']}
                />
                <Bar 
                  dataKey="faturamento" 
                  fill="#7C3AED" 
                  name="Faturamento"
                  radius={[8, 8, 0, 0]}
                >
                  <LabelList 
                    dataKey="faturamento" 
                    position="top" 
                    style={{ fill: '#fff', fontSize: '12px', fontWeight: 'bold' }}
                    formatter={(value) => `R$ ${(value / 1000).toFixed(1)}k`}
                  />
                </Bar>
                <Bar 
                  dataKey="lucro" 
                  fill="#3B82F6" 
                  name="Lucro Líquido"
                  radius={[8, 8, 0, 0]}
                >
                  <LabelList 
                    dataKey="lucro" 
                    position="top" 
                    style={{ fill: '#fff', fontSize: '12px', fontWeight: 'bold' }}
                    formatter={(value) => `R$ ${(value / 1000).toFixed(1)}k`}
                  />
                </Bar>
              </RechartsBarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Score de Saúde Financeira removido para simplificar */}

        {/* Card DFC - Largura Total (abaixo de Evolução, acima de DRE) */}
        <div className="mt-6">
          <DFCResumoCard companyId={selectedCompany?.id} fullWidth={true} />
        </div>

        {/* DRE (Serviços) - Largura Total */}
        <div className="mt-6">
          <DREChart companyId={selectedCompany?.id} />
        </div>

        {/* Gráficos de Contas a Pagar e Receber */}
        {!isFuncionarioSis && <div className="mt-6 mb-6">
          <SectionTitle
            icon={Target}
            title="Análise de Contas"
            helpTitle={HELP_CONTENT.graficos_contas.title}
            helpDescription={HELP_CONTENT.graficos_contas.description}
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Markup/BDI - Últimos 12 Meses */}
            <MarkupDonutChart
              key={markupRefreshKey}
              companyId={selectedCompany?.id}
              onConfigClick={() => setShowMarkupModal(true)}
            />

            {/* Contas a Pagar por Categoria */}
            {contasPagarPorCategoria.length > 0 && (
              <Card className="glass border-white/10 border-l-4 border-l-red-500">
                <CardHeader>
                  <CardTitle className="text-white text-base">Contas a Pagar por Categoria</CardTitle>
                  <p className="text-xs text-gray-400 mt-1">Mês atual</p>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={contasPagarPorCategoria}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="valor"
                        label={({ percentual }) => `${percentual}%`}
                        labelLine={false}
                      >
                        {contasPagarPorCategoria.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#EF4444', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4'][index % 5]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1F2937',
                          border: '1px solid #EF4444',
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}
                        formatter={(value) => [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Valor']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2 max-h-[120px] overflow-y-auto">
                    {contasPagarPorCategoria.map((item, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: ['#EF4444', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4'][index % 5] }}
                          />
                          <span className="text-gray-300 text-xs">{item.categoria}</span>
                        </div>
                        <span className="text-white font-medium text-xs">
                          R$ {(item.valor / 1000).toFixed(1)}k
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contas a Receber por Cliente */}
            {contasReceberPorCliente.length > 0 && (
              <Card className="glass border-white/10 border-l-4 border-l-green-500">
                <CardHeader>
                  <CardTitle className="text-white text-base">Contas a Receber por Cliente</CardTitle>
                  <p className="text-xs text-gray-400 mt-1">Mês atual</p>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={contasReceberPorCliente}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="valor"
                        label={({ percentual }) => `${percentual}%`}
                        labelLine={false}
                      >
                        {contasReceberPorCliente.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#10B981', '#3B82F6', '#7C3AED', '#F59E0B', '#EC4899'][index % 5]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1F2937',
                          border: '1px solid #10B981',
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}
                        formatter={(value) => [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Valor']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2 max-h-[120px] overflow-y-auto">
                    {contasReceberPorCliente.map((item, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: ['#10B981', '#3B82F6', '#7C3AED', '#F59E0B', '#EC4899'][index % 5] }}
                          />
                          <span className="text-gray-300 text-xs">{item.cliente}</span>
                        </div>
                        <span className="text-white font-medium text-xs">
                          R$ {(item.valor / 1000).toFixed(1)}k
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>}

        {/* Análise Detalhada e Alertas removidos para simplificar */}

        {/* Modal Criar Empresa */}
        <Dialog open={showCompanyModal} onOpenChange={setShowCompanyModal}>
          <DialogContent className="glass border-white/10" data-testid="create-company-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Criar Empresa</DialogTitle>
              <DialogDescription className="text-gray-400">
                Para começar, crie sua primeira empresa
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={createCompany} className="space-y-4" data-testid="create-company-form">
              <div>
                <Label htmlFor="company-name" className="text-gray-300">Nome da Empresa</Label>
                <Input
                  id="company-name"
                  data-testid="company-name-input"
                  value={companyData.name}
                  onChange={(e) => setCompanyData({ ...companyData, name: e.target.value })}
                  required
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label htmlFor="company-segment" className="text-gray-300">Segmento</Label>
                <Input
                  id="company-segment"
                  data-testid="company-segment-input"
                  value={companyData.segment}
                  onChange={(e) => setCompanyData({ ...companyData, segment: e.target.value })}
                  required
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <Button
                type="submit"
                data-testid="create-company-submit-button"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                {loading ? 'Criando...' : 'Criar Empresa'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Análise IA */}
        {/* Modal de Análise IA removido */}

        {/* Modal de Configuração de Markup */}
        <MarkupConfigModal
          open={showMarkupModal}
          onClose={() => setShowMarkupModal(false)}
          companyId={selectedCompany?.id}
          onSave={() => {
            setShowMarkupModal(false);
            setMarkupRefreshKey(prev => prev + 1);
          }}
        />

      </div>
      
      {/* Glossário Financeiro removido */}
    </div>
  );
};

export default Dashboard;